import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import LanguageToggle from './LanguageToggle';

const Navigation = () => {
  const { t } = useLanguage();
  const { settings } = useSiteSettings();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const allLinks = [
    { to: '/', label: t.navHome, visible: true },
    { to: '/instruments', label: t.navInstruments, visible: settings.page_instruments_visible },
    { to: '/atelier', label: t.navAtelier || t.navAbout, visible: settings.page_atelier_visible },
    { to: '/contact', label: t.navContact, visible: settings.page_contact_visible },
  ];

  const links = allLinks.filter(l => l.visible);
  const isActive = (path: string) => location.pathname === path;

  // On homepage before scroll: transparent bg, white text on mobile
  const isTransparent = isHome && !isScrolled && !isOpen;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isTransparent 
          ? 'bg-background/50 backdrop-blur-sm lg:bg-transparent lg:backdrop-blur-none' 
          : 'bg-background/80 backdrop-blur-md'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between" aria-label="Main navigation">
        {/* Brand */}
        <Link 
          to="/" 
          className="font-serif text-2xl tracking-wide transition-colors text-foreground hover:text-foreground/80"
        >
          Szoradi Instruments
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-8">
          {links.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-xs tracking-[0.2em] uppercase font-sans transition-colors duration-200 link-underline ${
                isActive(link.to)
                  ? (isTransparent ? 'text-foreground' : 'text-foreground')
                  : (isTransparent ? 'text-foreground/60 hover:text-foreground' : 'text-muted-foreground hover:text-foreground')
              }`}
            >
              {link.label}
            </Link>
          ))}
          <LanguageToggle />
        </div>

        {/* Mobile toggle */}
        <div className="flex items-center gap-3 lg:hidden">
          <LanguageToggle />
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 transition-colors text-foreground"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {isOpen && (
        <div className="lg:hidden bg-background/98 backdrop-blur-md">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsOpen(false)}
                className={`text-sm tracking-[0.15em] uppercase font-sans py-2 transition-colors ${
                  isActive(link.to) ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
